import React, { useContext, useState, useRef } from 'react';
import { AppContext } from '../App';

const DEMO_RESULT = {
  summary: "This appears to be a dated kitchen with original cabinets, laminate countertops, and worn vinyl flooring. Scope likely includes cabinet replacement or refacing, new countertops, and flooring.",
  scopeItems: [
    { item: "Cabinet replacement", icon: "🪵", estLow: 4500, estHigh: 8500 },
    { item: "Countertop install", icon: "🔲", estLow: 1800, estHigh: 3600 },
    { item: "Flooring replacement", icon: "🧱", estLow: 1200, estHigh: 2400 },
    { item: "Paint & finishing", icon: "🎨", estLow: 600, estHigh: 1200 }
  ],
  totalLow: 8100,
  totalHigh: 15700,
  confidence: "medium",
  notes: "Actual cost depends on exact square footage, cabinet material chosen, and whether plumbing/electrical needs updating."
};

function fmt(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export default function AIEstimate() {
  const { demoMode, showToast, navigate } = useContext(AppContext);
  const fileRef = useRef();
  const cameraRef = useRef();

  const [stage, setStage] = useState('idle'); // idle | loading | result | error
  const [preview, setPreview] = useState(null);
  const [contextNote, setContextNote] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      setPreview(e.target.result);
      runEstimate(e.target.result, file.type);
    };
    reader.readAsDataURL(file);
  };

  const runEstimate = async (dataUrl, mediaType) => {
    setStage('loading');

    if (demoMode) {
      await new Promise(r => setTimeout(r, 1800));
      setResult(DEMO_RESULT);
      setStage('result');
      return;
    }

    try {
      const base64 = dataUrl.split(',')[1];

      // Calls your Lambda function: estimateFromPhoto
      // Configure the endpoint in src/aws-config.js
      const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64: base64,
          mediaType,
          contextNote
        })
      });

      if (!response.ok) throw new Error('Estimate request failed');
      const data = await response.json();
      setResult(data);
      setStage('result');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not analyze photo');
      setStage('error');
    }
  };

  const reset = () => {
    setStage('idle');
    setPreview(null);
    setResult(null);
    setContextNote('');
    setError('');
  };

  const useEstimate = () => {
    showToast('Opening new Work Order with these line items...');
    navigate('add'); // In the full app, pass result.scopeItems as prefill
  };

  return (
    <div className="fade-in">
      <div className="screen-header" style={{ paddingBottom: 8 }}>
        <div>
          <div className="screen-title">AI ESTIMATE</div>
          <div className="screen-subtitle">Photo-based scope & cost range</div>
        </div>
      </div>

      <div className="demo-banner" style={{ margin: '0 16px 12px' }}>
        ⚡ Atlanta, GA market rates used for estimates
      </div>

      {stage === 'idle' && (
        <>
          <div className="scan-area" onClick={() => fileRef.current?.click()}>
            <div className="scan-area__icon">📸</div>
            <div className="scan-area__title">Take or upload a photo</div>
            <div className="scan-area__sub">Of the room, area, or job site</div>
          </div>

          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])} />

          <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            <button className="btn btn--primary" onClick={() => cameraRef.current?.click()}>
              📷 Take Photo Now
            </button>
            <button className="btn btn--secondary" onClick={() => fileRef.current?.click()}>
              🖼️ Choose from Library
            </button>
          </div>

          <div className="card">
            <label className="form-label">Optional Context</label>
            <textarea
              className="form-input"
              placeholder="e.g. 'Full kitchen remodel' or 'just the deck needs repair'"
              value={contextNote}
              onChange={e => setContextNote(e.target.value)}
              rows={2}
              style={{ marginTop: 8 }}
            />
          </div>

          <div className="card" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            This gives a rough ballpark, not a formal bid. Always confirm with your own measurements and walkthrough before quoting a customer.
          </div>
        </>
      )}

      {stage === 'loading' && (
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          {preview && (
            <img src={preview} alt="Job site" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 12, opacity: 0.5, marginBottom: 20 }} />
          )}
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Analyzing photo...</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Estimating scope and Atlanta market pricing</div>
        </div>
      )}

      {stage === 'result' && result && (
        <div style={{ padding: '0 16px' }}>
          {preview && (
            <img src={preview} alt="Job site" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 12, marginBottom: 14 }} />
          )}

          <div className="card">
            <div className="form-label" style={{ marginBottom: 8 }}>What I See</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{result.summary}</div>
            <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: confColor(result.confidence) }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: confColor(result.confidence), display: 'inline-block' }} />
              {(result.confidence || 'medium').toUpperCase()} CONFIDENCE
            </div>
          </div>

          <div style={{
            background: 'rgba(230,126,58,0.08)', border: '1px solid rgba(230,126,58,0.25)',
            borderRadius: 12, padding: 18, textAlign: 'center', marginBottom: 14
          }}>
            <div style={{ fontSize: 11, color: 'var(--accent-orange)', letterSpacing: 1, marginBottom: 6, textTransform: 'uppercase' }}>
              Estimated Cost Range
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 30, fontWeight: 500 }}>
              {fmt(result.totalLow)} – {fmt(result.totalHigh)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Based on Atlanta, GA market rates</div>
          </div>

          <div className="card">
            <div className="form-label" style={{ marginBottom: 8 }}>Scope of Work Detected</div>
            {(result.scopeItems || []).map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i < result.scopeItems.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>{s.icon || '🔧'}</div>
                <div style={{ flex: 1, fontSize: 13 }}>{s.item}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--accent-orange)', flexShrink: 0 }}>
                  {fmt(s.estLow)}–{fmt(s.estHigh)}
                </div>
              </div>
            ))}
          </div>

          {result.notes && (
            <div className="card" style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              ⚠️ {result.notes}
            </div>
          )}

          <div className="card" style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            This is an AI-generated rough estimate for reference only — not a formal bid. Always confirm with your own measurements, material costs, and walkthrough before quoting a customer.
          </div>

          <button className="btn btn--primary" onClick={useEstimate} style={{ marginBottom: 10 }}>
            Create Work Order from This
          </button>
          <button className="btn btn--secondary" onClick={reset}>
            Scan Another Photo
          </button>
        </div>
      )}

      {stage === 'error' && (
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Couldn't Analyze Photo</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>{error}</div>
          <button className="btn btn--primary" onClick={reset}>Try Again</button>
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}

function confColor(level) {
  if (level === 'high') return 'var(--accent-green)';
  if (level === 'low') return 'var(--accent-red)';
  return 'var(--accent-yellow)';
}
